import { useCallback, useEffect, useRef, useState } from 'react';

import { getTenantDomains } from '../api/tenant-domains.api';
import { useCreateTenantDomain } from './useCreateTenantDomain';
import { useUpdateTenantDomain } from './useUpdateTenantDomain';
import type {
  CreateTenantDomainInput,
  TenantDomain,
  UpdateTenantDomainStateInput,
} from '../types/tenant-domain.types';

export function useTenantDomains(tenantId: number | string | undefined) {
  const [domains, setDomains] = useState<TenantDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);
  const createDomain = useCreateTenantDomain(tenantId ?? '');
  const updateDomain = useUpdateTenantDomain(tenantId ?? '');

  const loadDomains = useCallback(() => {
    const generation = ++requestGenerationRef.current;
    const isCurrentRequest = () =>
      mountedRef.current && requestGenerationRef.current === generation;

    if (tenantId === undefined) {
      setDomains([]);
      setLoading(false);
      return Promise.resolve([] as TenantDomain[]);
    }

    setLoading(true);
    setError(null);

    return getTenantDomains(tenantId)
      .then((data) => {
        if (isCurrentRequest()) setDomains(data);
        return data;
      })
      .catch((requestError) => {
        if (isCurrentRequest()) {
          setDomains([]);
          setError('خطا در دریافت دامنه‌های تننت');
        }
        throw requestError;
      })
      .finally(() => {
        if (isCurrentRequest()) setLoading(false);
      });
  }, [tenantId]);

  const create = useCallback(async (input: CreateTenantDomainInput) => {
    try {
      const domain = await createDomain.submit(input);
      await loadDomains();
      return domain;
    } catch (requestError) {
      // A duplicate-domain response means the existing record is still
      // useful to the operator. Refresh it without masking the original
      // error message surfaced by useCreateTenantDomain.
      await loadDomains().catch(() => undefined);
      throw requestError;
    }
  }, [createDomain.submit, loadDomains]);

  const update = useCallback(async (
    domainId: number | string,
    input: UpdateTenantDomainStateInput,
  ) => {
    const domain = await updateDomain.submit(domainId, input);
    await loadDomains();
    return domain;
  }, [loadDomains, updateDomain.submit]);

  useEffect(() => {
    mountedRef.current = true;
    void loadDomains().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [loadDomains]);

  return {
    domains,
    loading,
    error,
    submitting: createDomain.submitting || updateDomain.submitting,
    mutationError: createDomain.error || updateDomain.error,
    create,
    update,
    refetch: loadDomains,
  };
}
